import { useState, useEffect, useRef } from "react";
import {
  Box,
  Paper,
  Text,
  Flex,
  Button,
  Table,
  ThemeIcon,
  ActionIcon,
  Loader,
  Center,
  FileButton,
  Divider,
  Stack,
} from "@mantine/core";
import { IconFileText, IconDownload, IconUpload } from "@tabler/icons-react";
import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import { successNotification } from "@/shared/utils/successNotification";
import { errorNotification } from "@/shared/utils/errorNotification";
import NdaPage from "@/pages/nda/NdaPage";

type SharedDocumentsTabProps = {
  matchId: string;
  userCompanyName?: string;
  ndaOtherCompanyId?: string | null;
  ndaCollaboration?: unknown;
  collaborationReadOnly?: boolean;
};

type SharedDocument = Awaited<ReturnType<typeof trpc.collaboration.listDocuments.query>>[number];

export default function SharedDocumentsTab({
  matchId,
  userCompanyName,
  ndaOtherCompanyId,
  ndaCollaboration,
  collaborationReadOnly = false,
}: SharedDocumentsTabProps) {
  const [documents, setDocuments] = useState<SharedDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  //   const [deletingId, setDeletingId] = useState<string | null>(null);
  const resetRef = useRef<() => void>(null);
  const [ndaPanelVisible, setNdaPanelVisible] = useState(false);

  const ndaCollabId = (ndaCollaboration as { id?: string } | undefined)?.id;
  useEffect(() => {
    setNdaPanelVisible(false);
  }, [matchId, ndaCollabId]);

  const fetchDocuments = async () => {
    try {
      const docs = await trpc.collaboration.listDocuments.query({ matchId });
      setDocuments(docs);
    } catch (err) {
      handleTrpcError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, [matchId]);

  const handleUpload = async (file: File | null) => {
    if (!file) return;

    const maxSize = 10 * 1024 * 1024; // 10MB

    if (file.size > maxSize) {
      errorNotification({
        title: "File too large",
        message: "File size must be less than 10MB",
      });
      resetRef.current?.();
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("matchId", matchId);
      formData.append("name", file.name);

      await trpc.collaboration.shareDocuments.mutate(formData as any);

      successNotification({
        title: "Success",
        message: "Document uploaded successfully",
      });

      await fetchDocuments();
    } catch (err) {
      handleTrpcError(err);
    } finally {
      setUploading(false);
      resetRef.current?.();
    }
  };

  const handleDownload = async (doc: SharedDocument) => {
    setDownloadingId(doc.id);

    try {
      const result = await trpc.collaboration.downloadSharedDocuments.mutate({ sharedDocId: doc.id, download: true });

      const link = document.createElement("a");
      link.href = result.url;
      link.download = result.fileName;
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      handleTrpcError(err);
    } finally {
      setDownloadingId(null);
    }
  };

  //   const handleDelete = async (docId: string) => {
  //     setDeletingId(docId);

  //     try {
  //       await trpc.collaboration.deleteSharedDocument.mutate({ sharedDocId: docId });

  //       successNotification({
  //         title: "Success",
  //         message: "Document deleted successfully",
  //       });

  //       setDocuments(documents.filter((doc) => doc.id !== docId));
  //     } catch (err) {
  //       handleTrpcError(err);
  //     } finally {
  //       setDeletingId(null);
  //     }
  //   };

  const formatDate = (date: string | Date) => {
    return new Date(date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  if (loading) {
    return (
      <Center py="xl">
        <Loader size="lg" />
      </Center>
    );
  }

  const showNda = Boolean(ndaOtherCompanyId && ndaCollaboration);

  return (
    <Stack gap="xl">
      <Box>
        {/* Header */}
        <Flex justify="space-between" align="flex-start" mb="lg" direction={{ base: "column", sm: "row" }} gap="md">
          <Box>
            <Text fw={400} fz="lg" mb={4}>
              Shared Documents
            </Text>
            <Text fz="sm" c="slate.6">
              Select documents from your company profile to share in this collaboration
            </Text>
          </Box>
          {!collaborationReadOnly ? (
            <FileButton
              resetRef={resetRef}
              onChange={handleUpload}
              accept="application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,image/png,image/jpeg,image/jpg,image/webp"
            >
              {(props) => (
                <Button
                  leftSection={<IconUpload size={16} />}
                  loading={uploading}
                  w={{ base: "100%", sm: "auto" }}
                  {...props}
                >
                  Add Documents
                </Button>
              )}
            </FileButton>
          ) : (
            <Text fz="sm" c="dimmed">
              Uploads are closed for this collaboration.
            </Text>
          )}
        </Flex>

        {/* Documents Table or Empty State */}
        {documents.length > 0 ? (
          <Box style={{ overflowX: "auto" }}>
            <Table verticalSpacing="lg" horizontalSpacing="xl">
              <Table.Thead>
                <Table.Tr style={{ borderBottom: "2px solid var(--mantine-color-primary-6)" }}>
                  <Table.Th>
                    <Text fz="sm" fw={400} c="slate.6" tt="uppercase">
                      Document
                    </Text>
                  </Table.Th>
                  <Table.Th>
                    <Flex align="center" gap="md">
                      <Divider orientation="vertical" size="sm" h="xl" />
                      <Text fz="sm" fw={400} c="slate.6" tt="uppercase">
                        Shared By
                      </Text>
                    </Flex>
                  </Table.Th>
                  <Table.Th>
                    <Flex align="center" gap="md">
                      <Divider orientation="vertical" size="sm" h="xl" />
                      <Text fz="sm" fw={400} c="slate.6" tt="uppercase">
                        Shared On
                      </Text>
                    </Flex>
                  </Table.Th>
                  <Table.Th>
                    <Flex align="center" gap="md">
                      <Divider orientation="vertical" size="sm" h="xl" />
                      <Text fz="sm" fw={400} c="slate.6" tt="uppercase">
                        Actions
                      </Text>
                    </Flex>
                  </Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {documents.map((doc) => (
                  <Table.Tr key={doc.id}>
                    <Table.Td>
                      <Flex align="center" gap="sm">
                        <ThemeIcon size={36} radius="md" color="primary" variant="light">
                          <IconFileText size={18} />
                        </ThemeIcon>
                        <Text fz="sm">{doc.fileName}</Text>
                      </Flex>
                    </Table.Td>
                    <Table.Td pl={42}>
                      <Text fz="sm" c="slate.6">
                        {doc.isOwnDocument ? userCompanyName || "Your Company" : doc.sharedBy.legalName}
                      </Text>
                    </Table.Td>
                    <Table.Td pl={42}>
                      <Text fz="sm" c="slate.6">
                        {formatDate(doc.sharedAt)}
                      </Text>
                    </Table.Td>
                    <Table.Td pl={42}>
                      <Flex gap="xs">
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          size="lg"
                          onClick={() => handleDownload(doc)}
                          loading={downloadingId === doc.id}
                        >
                          <IconDownload size={18} />
                        </ActionIcon>
                        {/* {doc.isOwnDocument && (
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          size="lg"
                          onClick={() => handleDelete(doc.id)}
                          loading={deletingId === doc.id}
                        >
                          <IconTrash size={18} />
                        </ActionIcon>
                      )} */}
                      </Flex>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Box>
        ) : collaborationReadOnly ? (
          <Paper withBorder radius="lg" p="xl">
            <Text fz="sm" c="dimmed" ta="center">
              No shared documents yet. Uploads are closed for this collaboration.
            </Text>
          </Paper>
        ) : (
          /* Empty State */
          <FileButton
            resetRef={resetRef}
            onChange={handleUpload}
            accept="application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,image/png,image/jpeg,image/jpg,image/webp"
          >
            {(props) => (
              <Paper
                withBorder
                radius="lg"
                p="xl"
                style={{
                  cursor: "pointer",
                  borderStyle: "dashed",
                }}
                {...props}
              >
                <Flex direction="column" align="center" gap="sm" py="xl">
                  <ThemeIcon size={48} radius="xl" color="primary" variant="light">
                    <IconUpload size={24} />
                  </ThemeIcon>
                  <Text fz="sm" c="slate.7">
                    Click to upload or drag and drop
                  </Text>
                  <Text fz="xs" c="slate.5">
                    PDF, Doc, Excel, or Images • Max 10MB
                  </Text>
                  {uploading && <Loader size="sm" mt="sm" />}
                </Flex>
              </Paper>
            )}
          </FileButton>
        )}
      </Box>

      {showNda ? (
        <>
          <Divider />
          <Box>
            <Flex justify="space-between" align={{ base: "stretch", sm: "flex-start" }} gap="md" direction={{ base: "column", sm: "row" }} mb="md">
              <Box style={{ flex: 1 }}>
                <Text fw={400} fz="lg" mb={4}>
                  NDA
                </Text>
                <Text fz="sm" c="slate.6">
                  Non-disclosure agreement shared between your company and your partner for this collaboration.
                </Text>
              </Box>
              {!ndaPanelVisible ? (
                <Button variant="filled" color="primary" onClick={() => setNdaPanelVisible(true)} style={{ flexShrink: 0 }}>
                  View NDA
                </Button>
              ) : (
                <Button variant="default" onClick={() => setNdaPanelVisible(false)} style={{ flexShrink: 0 }}>
                  Hide NDA
                </Button>
              )}
            </Flex>
            {ndaPanelVisible ? (
              <NdaPage
                embedded
                otherCompanyId={ndaOtherCompanyId!}
                collaboration={ndaCollaboration as any}
                collaborationReadOnly={collaborationReadOnly}
              />
            ) : null}
          </Box>
        </>
      ) : null}
    </Stack>
  );
}
